import { CardWithActions, StyledButton } from "@/components";
import FormikTextInput from "@/components/forms/FormikTextInput";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import { Box, Grid, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import FormikDatePicker from "@/components/forms/FormikDatePicker";
import { useFetchSettingsQuery } from "@/api/services/Settings/menu/Menu";
import initialValuesFromFile from "./initialValues";
import validationSchema from "./validationSchema";
import { useNavigate } from "react-router-dom";
import {
  useAddEmployeeMutation,
  useFetchEmployeesByParentOrgQuery,
  useGetDomainBasedOrgListQuery,
  useGetOrganizationQuery,
  useGetRecruiterQuery,
  useGetRwrByRmMutation,
  useRoleTypeQuery,
} from "@/api/services/employeeList/EmployeeList";
import mapOptions from "@/utils/mappers/mapOptions";
import { useFetchSettingsRowsQuery,useFetchModuleSettingsDataQuery } from "@/api/services/Settings/business/Business";
import dayjs from "dayjs";
import { toast } from "@/components/toastify";
import { useFetchGradeQuery } from "@/api/services/moduleSettingsMaster/module_Setting_Master/GradeApi";
import { InfoOutlineIcon } from "@/icons";
import ShiftModal from "./ShiftModal";
import { useFetchShiftMasterQuery } from "@/api/services/attendanceMaster/ShiftMaster";
import { useFetchLeaveSchemeQuery } from "@/api/services/leaveManagement/LeaveTypeCategory";

const AddEmployeeForm = ({ onBackClick }) => {
  const navigate = useNavigate();
  const [getReviewerTrigger, { data: reviewerData, isLoading, isError }] =
    useGetRwrByRmMutation();
  const { data: fetchShift = [] } = useFetchShiftMasterQuery();

  const { data: responses = {} } =
    useFetchSettingsRowsQuery("Experience Years");
  const experienceYearOptions = (responses.data || []).map((item) => ({
    label: `${item.name} ${item.name === "1" ? "Year" : "Years"}`,
    value: Number(item.value),
  }));

  // email domain based org list
  const { data: domainOrgList = [] } = useGetDomainBasedOrgListQuery();

  const { data: assetResponse = {} } = useFetchSettingsRowsQuery("Asset");
  const { data: probationMonthResponse = {} } =
    useFetchSettingsRowsQuery("Probation Months");

  const probationMonthsOptions = (probationMonthResponse.data || [])
    .filter((item) => item.status === "Active") // remove inactive
    .map((item) => ({
      label: `${item.name} ${
        item.name === "1" || item.name === "0" ? "Month" : "Months"
      }`,
      value: Number(item.value),
    }));

  const assetOptions = (assetResponse.data || []).map((item) => ({
    label: item.name,
    value: item.value,
  }));

  const { data: expMonthResponse = {} } =
    useFetchSettingsRowsQuery("Experience Months");

  const experienceMonthOptions = (expMonthResponse.data || []).map((item) => ({
    label: `${item.name} ${item.name === "1" ? "Month" : "Months"}`,
    value: Number(item.value),
  }));

  // Mutation hook for fetching division by department
  // const [getDivisionHeadTrigger, { data: divisionData }] =
  //   useGetDivisionByDeptMutation();

  // Mutation hook for adding an employee
  const [submitForm, { isLoading: isSubmitting }] = useAddEmployeeMutation();

  // get organizations
  const { data: fetchOrganization = [] } = useGetOrganizationQuery({
    isParent: "1",
  });

  // Get default org value safely
  const defaultOrgValue = fetchOrganization?.[0]?.value || "";

  // Extract active organization from localStorage
  const localStorageData = useMemo(() => {
    return JSON.parse(localStorage.getItem("loginDetails")) || {};
  }, []);

  const roleType = localStorageData?.userDetails.roleType;

  const organizationAccess =
    localStorageData?.userDetails?.organizationAccess || [];

  const subOrgOptions = organizationAccess.map((org) => ({
    label: org.organizationDetail, // visible to user in dropdown
    value: org.organizationCode, // this will be submitted
  }));

  // Memoize final initialValues

  const activeOrg = localStorageData?.userDetails?.activeOrganization;
  const initialValues = useMemo(() => {
    return {
      ...initialValuesFromFile,
      organization: defaultOrgValue,
      subOrganization: activeOrg?.organizationCode || "",
    };
  }, [defaultOrgValue, activeOrg]);

  // recruiters, On boarder, buddy => 3
  const { data: recruiterOptions = [] } = useGetRecruiterQuery("Recruiter");
  const { data: buddyOptions = [] } = useGetRecruiterQuery("Buddy");

  const { data: fetchAllEmployee } = useFetchEmployeesByParentOrgQuery();

  // probation months
  // const probationMonthsOptions = [
  //   { label: "1 Month", value: "1" },
  //   { label: "3 Months", value: "3" },
  //   { label: "6 Months", value: "6" },
  // ];

  const fetchAllEmployees = useMemo(() => {
    return mapOptions(fetchAllEmployee);
  }, [fetchAllEmployee]);

  // role type
  const { data: roleTypeOptions = [] } = useRoleTypeQuery() || {};

  const { data: workLocationOptions = [] } = useFetchSettingsQuery({
    referenceName: "Work Location",
  });
  // const { data: roleFamily = [] } = useFetchModuleSettingsDataQuery(
  //   "RoleFamily"
  // );
  const { data: departmentOptions = [] } = useFetchSettingsQuery({
    referenceName: "Department",
  });
  const { data: designationOptions = [] } = useFetchSettingsQuery({
    referenceName: "Designation",
  });

  const { data: weekOffOptions = [] } = useFetchSettingsQuery({
    referenceName: "Week Off",
  });
  const { data: leaveSchemeResponse } = useFetchLeaveSchemeQuery();
  const leaveScheme = leaveSchemeResponse?.data || [];

  const leaveSchemeOptions = leaveScheme.map((scheme) => ({
    label: scheme.schemeName,
    value: scheme.schemeName,
  }));

  const { data: attendanceFormatOptions = [] } = useFetchSettingsQuery({
    referenceName: "Attendance Format",
  });
  const { data: experienceOptions = [] } = useFetchSettingsQuery({
    referenceName: "Experience",
  });

  // fetch grade by org
  const { data } = useFetchGradeQuery();
  const gradeOptions = data?.data || [];

  const gradeOptionsFormatted = gradeOptions?.map((grade) => ({
    label: grade.gradeName,
    value: grade.gradeName,
  }));

  const { data: genderOptions = [] } = useFetchSettingsQuery({
    referenceName: "Gender",
  });
  const { data: maritalStatusOptions = [] } = useFetchSettingsQuery({
    referenceName: "Marital Status",
  });
  const { data: bloodGroupOptions = [] } = useFetchSettingsQuery({
    referenceName: "Blood Group",
  });
  // const { data: shiftOptions = [] } = useFetchSettingsQuery({
  //   referenceName: "Shift",
  // });
  const { data: applyPfOptions = [] } = useFetchSettingsQuery({
    referenceName: "PF",
  });
  const { data: swipeMethodOptions = [] } = useFetchSettingsQuery({
    referenceName: "Swipe Method",
  });

  const { data: marketFacingOptions = [] } = useFetchSettingsQuery({
    referenceName: "Market Facing Title",
  });

  // man power outsourcing
  const { data: response = {} } = useFetchSettingsRowsQuery("Yes or No");
  const trueOrFalse = response?.data || [];

  // division head option

  // const mappedDivisionData = useMemo(() => {
  //   return divisionData ? mapOptions(divisionData) : [];
  // }, [divisionData]);

  // const divisionHeadOptions = useMemo(() => {
  //   return mappedDivisionData?.length > 0
  //     ? mappedDivisionData
  //     : fetchAllEmployees || [];
  // }, [mappedDivisionData, fetchAllEmployees]);

  // Function to format options
  const formatOptions = (options = []) =>
    options?.map((item) => ({
      value: item.value,
      label: item.name,
    }));
  const probationTypeOptions = [
    {
      label: "Trainee",
      value: "Trainee",
    },
    {
      label: "Probation",
      value: "Probation",
    },

    {
      label: "Confirmed",
      value: "Confirmed",
    },
  ];

  const employeeTypeOptions = [
    { label: "New Employee", value: "New Employee" },
    { label: "On Role", value: "On Role" },
    { label: "Transfer", value: "Transfer" },
  ];

//   const rolefamily=[
//   {value:"Frontend Developer",label:"Frontend Developer"},
//   {value:"Backend Devleoper",label:"Backend Developer"},
//   {value:"Testing",label:"Testing"},
//   {value:"Hr",label:"Hr"},
// ];

  const trueOrFalseFormatted = formatOptions(trueOrFalse);

  const workLocationOptionsFormatted = formatOptions(workLocationOptions);
  const departmentOptionsFormatted = formatOptions(departmentOptions);
  const designationOptionsFormatted = formatOptions(designationOptions);
  // const roleFamilyOptionsFormatted = formatOptions(roleFamily);
  const attendanceFormatOptionsFormatted = formatOptions(
    attendanceFormatOptions
  );
  const experienceOptionsFormatted = formatOptions(experienceOptions);
  // const gradeOptionsFormatted = formatOptions(gradeOptions);
  const genderOptionsFormatted = formatOptions(genderOptions);
  const maritalStatusOptionsFormatted = formatOptions(maritalStatusOptions);
  const bloodGroupOptionsFormatted = formatOptions(bloodGroupOptions);

  // const shiftOptionsFormatted = formatOptions(shiftOptions);
  const applyPfOptionsFormatted = formatOptions(applyPfOptions);

  const swipeMethodOptionsFormatted = formatOptions(swipeMethodOptions);
  const marketFacing = formatOptions(marketFacingOptions);
  // const divisionHeadOptionsFormatted = formatOptions(divisionHeadOptions);

  // Callback function for handling form submission
  const handleSubmit = useCallback(
    async (values, actions) => {
      try {
        const formattedValues = {
          ...values,
          firstName:
            values.firstName.charAt(0).toUpperCase() +
            values.firstName.slice(1),
          primaryContactNumber: values.primaryContactNumber
            ? parseInt(values.primaryContactNumber)
            : "",
          secondaryContactNumber:
            values.secondaryContactNumber?.trim() !== ""
              ? parseInt(values.secondaryContactNumber)
              : "",
          doj: values.doj ? dayjs(values.doj).format("DD/MM/YYYY") : "",
          dob: values.dob ? dayjs(values.dob).format("DD/MM/YYYY") : "",
          accessType: ["Employee"],
          ctc: roleType === "HRSS" ? 0 : parseInt(values.ctc || 0),
          // roleFamily : values.rolefamily || " ",
        };

        console.log("Formatted Values:", formattedValues);

        await submitForm({
          formName: "Add Employee",
          formFields: formattedValues,
        }).unwrap();

        toast("success", "Employee added successfully");
        actions.resetForm();
        // navigate("/employeeList");
        onBackClick(); // Navigate back after successful submission
      } catch (err) {
        actions.setSubmitting(false);
        // toast("error", err.data?.message || "Form submission failed.");
      }
    },
    [submitForm, navigate, roleType]
  );

  const [shiftModalOpen, setShiftModalOpen] = useState(false);

  const handleShiftClick = () => {
    setShiftModalOpen(true);
  };

  return (
    <Box>
      <CardWithActions
        sx={{ backgroundColor: "#fff" }}
        backIcon={
          <ChevronLeftIcon
            sx={{
              cursor: "pointer",
              fontSize: 32,
              borderRadius: "50%",
              padding: "4px",
              marginRight: 1,
              transition: "border-color 0.3s",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.primary.main,
                color: (theme) => theme.palette.primary.contrastText,
              },
            }}
            onClick={onBackClick}
          />
        }
        headerTitle={"Add Employee"}
      >
        {/* {roleType} */}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values, actions) => {
            handleSubmit(values, actions);
          }}
          enableReinitialize
        >
          {({ values, setFieldValue }) => {
            // Auto-set reviewer field when reviewerData is available
            useEffect(() => {
              if (reviewerData && reviewerData.empId) {
                setFieldValue("reviewer", reviewerData.empId);
              }
            }, [reviewerData, setFieldValue]);

            return (
              <Form>
                <Box mb={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikTextInput
                        name="firstName"
                        label="First Name"
                        width="100%"
                        required
                        placeholder="First Name"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikTextInput
                        name="middleName"
                        label="Middle Name"
                        width="100%"
                        placeholder="Middle Name"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikTextInput
                        name="lastName"
                        label="Last Name"
                        width="100%"
                        required
                        placeholder="Last Name"
                      />
                    </Grid>

                    {/* Second Row */}
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="organization"
                        label="Organization"
                        placeholder="Select Organization"
                        required
                        search
                        options={mapOptions(fetchOrganization || [])}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="subOrganization"
                        label="Sub Organization"
                        placeholder="Select Sub Organization"
                        required
                        search
                        disabled
                        options={subOrgOptions || []}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikTextInput
                        name="rfh"
                        label="RFH"
                        width="100%"
                        // required
                        placeholder="Enter RFH"
                        inputRestriction="alphanumeric"
                      />
                    </Grid>

                    {/* 3rd Row */}

                    <Grid item xs={12} sm={6} md={4}>
                      <FormikTextInput
                        name="ticketId"
                        label="Ticket Id"
                        width="100%"
                        required={roleType == "HRSS" ? false : true}
                        placeholder="Enter Ticket Id"
                        inputRestriction="alphanumeric"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="department"
                        label="Department"
                        placeholder="Select Department"
                        required
                        options={departmentOptionsFormatted || []}
                        search
                        onChange={(e) => {
                          const department = e.target.value;

                          setFieldValue("Department", department);
                          // getDivisionHeadTrigger(department);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        search
                        name="designation"
                        label="Designation"
                        placeholder="Select Designation"
                        required
                        options={designationOptionsFormatted || []}
                      />
                    </Grid>

                    {/* <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        search
                        name="rolefamily"
                        label="Role Family"
                        placeholder="Select Role Family"
                        options={mapOptions(roleFamily?.data) || []}
                      />
                    </Grid> */}
                    

                    {/* 4rd Row */}

                    <Grid item xs={12} sm={6} md={4}>
                      <FormikTextInput
                        name="primaryContactNumber"
                        label="Primary Contact Number"
                        width="100%"
                        required
                        placeholder="Enter Primary Contact Number"
                        inputRestriction="numeric"
                        maxLength={10}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikTextInput
                        name="secondaryContactNumber"
                        label="Secondary Contact Number"
                        width="100%"
                        // required
                        placeholder="Enter Secondary Contact Number"
                        inputRestriction="numeric"
                        maxLength={10}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikTextInput
                        name="personalEmailId"
                        label="Personal Email"
                        width="100%"
                        required
                        placeholder="Enter Personal Email"
                        inputRestriction="alphanumeric"
                        specialCharacters={true}
                      />
                    </Grid>

                    {/* 5th Row */}

                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="roleOfIntake"
                        label="Role of Intake"
                        placeholder="Select Role of Intake"
                        required
                        options={mapOptions(roleTypeOptions) || []}
                        search
                        onChange={(e) => {
                          const selectedValue = e.target.value;
                          // setFieldValue("roleOfIntake", selectedValue);
                          console.log("Selected Role:", selectedValue);
                          if (
                            selectedValue === "NAPS" ||
                            selectedValue === "NATS" ||
                            selectedValue === "IC"
                          ) {
                            setFieldValue("grade", "TR" || "");
                            setFieldValue("probation", "Trainee" || "");
                          } else {
                            setFieldValue("grade", "");
                            setFieldValue("probation", "");
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="employeeType"
                        label="Employee Type"
                        placeholder="Select Employee Type "
                        required
                        search
                        options={employeeTypeOptions || []}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="attendanceFormat"
                        label="Attendance Format"
                        placeholder="Select Attendance Format"
                        required
                        search
                        options={attendanceFormatOptionsFormatted || []}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="weekOff"
                        label="Week off"
                        placeholder="Select Week off"
                        required
                        search
                        options={mapOptions(weekOffOptions) || []}
                      />
                    </Grid>
                    {/* <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="leaveScheme"
                        label="Leave Scheme"
                        placeholder="Select leave scheme"
                        required
                        search
                        options={mapOptions(leaveSchemeOptions) || []}
                      />
                    </Grid> */}
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="experience"
                        label="Experience"
                        placeholder="Select Experience"
                        required
                        options={experienceOptionsFormatted || []}
                        search
                        onChange={(e) => {
                          const selectedValue = e.target.value;
                          setFieldValue("experience", selectedValue);

                          if (selectedValue !== "Experienced") {
                            setFieldValue("years", "");
                            setFieldValue("months", "");
                          }
                        }}
                      />
                    </Grid>

                    {values.experience === "Experienced" && (
                      <Grid item xs={12} sm={6} md={4}>
                        <FormikSelectInput
                          name="years"
                          label="Experience Year"
                          placeholder="Select Experience Year"
                          required
                          options={experienceYearOptions || []}
                          search
                        />
                      </Grid>
                    )}
                    {values.experience === "Experienced" && (
                      <Grid item xs={12} sm={6} md={4}>
                        <FormikSelectInput
                          name="months"
                          label="Experience Month"
                          placeholder="Select Experience Month"
                          required
                          options={experienceMonthOptions || []}
                          search
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="grade"
                        label="Grade "
                        placeholder="Select Grade"
                        required
                        options={gradeOptionsFormatted || []}
                        onChange={(e) => {
                          const selectedValue = e.target.value;

                          const selectedGrade = gradeOptions?.find(
                            (g) => String(g.gradeName) === String(selectedValue)
                          );

                          if (selectedGrade) {
                            const isProbation =
                              selectedGrade.probationStatus === true;

                            setFieldValue(
                              "probation",
                              isProbation ? "Probation" : "Trainee"
                            );

                            setFieldValue(
                              "probationMonths",
                              isProbation
                                ? selectedGrade.probationDetail?.defaultDurationMonth?.toString() ||
                                    ""
                                : "0"
                            );

                            setFieldValue(
                              "noticePeriod",
                              selectedGrade.noticePeriod?.toString() || ""
                            );
                          }

                          setFieldValue("grade", selectedValue);
                        }}
                        search
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="probation"
                        label="Probation"
                        width="100%"
                        required
                        options={probationTypeOptions || []}
                        placeholder="Enter Probation"
                        onChange={(e) => {
                          if (e.target.value === "Confirmed") {
                            setFieldValue("probationMonths", "0");
                          }
                          // const selectedValue = e.target.value;
                          // setFieldValue("probation", selectedValue);
                        }}
                      />
                    </Grid>
                    {values.probation !== "Confirmed" && (
                      <Grid item xs={12} sm={6} md={4}>
                        <FormikSelectInput
                          name="probationMonths"
                          label="Probation Months"
                          placeholder="Select Probation Months"
                          required
                          search
                          options={probationMonthsOptions || []}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={4}>
                      <FormikTextInput
                        name="noticePeriod"
                        label="Notice Period"
                        placeholder="Select Notice Period"
                        required
                        search
                        inputRestriction="numeric"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="recruiterId"
                        // name="recruiter"
                        label="Recruiter"
                        placeholder="Select Recruiter"
                        required
                        search
                        options={mapOptions(recruiterOptions) || []}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="buddyId"
                        label="HRBP"
                        placeholder="Select HRBP"
                        required
                        search
                        options={mapOptions(buddyOptions) || []}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="primaryReportingManager"
                        label="Primary Reporting Manager"
                        placeholder="Select Primary Reporting Manager"
                        required
                        search
                        onChange={(e) => {
                          const selectedId = e.target.value;

                          setFieldValue("primaryReportingManager", selectedId);
                          if (selectedId) {
                            getReviewerTrigger(selectedId);
                          }
                        }}
                        options={fetchAllEmployees || []}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="reviewer"
                        label="Reviewer"
                        placeholder="Select Reviewer"
                        required
                        search
                        options={fetchAllEmployees || []}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="workLocation"
                        label="Work Location"
                        placeholder="Select Work Location"
                        required
                        options={workLocationOptionsFormatted || []}
                        search
                        onChange={(e) => {
                          const selectedValue = e.target.value;
                          setFieldValue("workLocation", selectedValue);

                          // Clear city only if not relevant
                          if (
                            ![
                              "Work From Home",
                              "Others - Plant / Field / Other Offices",
                            ].includes(selectedValue)
                          ) {
                            setFieldValue("workLocationCity", "");
                          }
                        }}
                      />
                    </Grid>
                    {[
                      "Work From Home",
                      "Others - Plant / Field / Other Offices",
                    ].includes(values.workLocation) && (
                      <Grid item xs={12} sm={6} md={4}>
                        <FormikTextInput
                          name="workLocationCity"
                          label="Work Location City"
                          placeholder="Enter Work Location City"
                          required={
                            values.workLocation ===
                              "Others - Plant / Field / Other Offices" ||
                            values.workLocation === "Work From Home"
                          }
                        />
                      </Grid>
                    )}

                    {roleType != "HRSS" && (
                      <Grid item xs={12} sm={6} md={4}>
                        <FormikTextInput
                          name="ctc"
                          label="CTC"
                          width="100%"
                          required
                          inputRestriction="numeric"
                          placeholder="Enter CTC"
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikDatePicker
                        name="dob"
                        label="DOB"
                        required
                        views={["year", "month", "day"]}
                        onChange={(value) => {
                          setFieldValue("dob", value); // value is a Date/dayjs object
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <FormikDatePicker
                        name="doj"
                        label="DOJ"
                        required
                        views={["year", "month", "day"]}
                        onChange={(value) => {
                          setFieldValue("doj", value); // value is a Date/dayjs object
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="gender"
                        label="Gender "
                        placeholder="Select Gender"
                        required
                        options={genderOptionsFormatted || []}
                        search
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="divisionHeadId"
                        label="Division Head ID"
                        placeholder="Select Division Head ID "
                        required
                        search
                        options={fetchAllEmployees || []}
                        // options={mapOptions(divisionHeadOptions) || []}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="swipeMethod"
                        label="Swipe Method"
                        placeholder="Select Swipe Method"
                        required
                        options={swipeMethodOptionsFormatted || []}
                        search
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="shift"
                        label="Shift"
                        icon={
                          <InfoOutlineIcon color="action" fontSize="small" />
                        }
                        placeholder="Select Shift "
                        required
                        options={fetchShift?.data?.map((c) => ({
                          value: c.shiftCode, // or c.name, whichever you want to store
                          label: c.shiftName,
                        }))}
                        search
                        onLabelClick={handleShiftClick}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="manpowerOutsourcing"
                        label="Manpower Outsourcing"
                        placeholder="Select Manpower Outsourcing"
                        required
                        options={trueOrFalseFormatted || []}
                        search
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="marketFacingTitle"
                        label="Market Facing Title"
                        placeholder="Select Market Facing Title "
                        required
                        options={marketFacing || []}
                        search
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="mailIdRequired"
                        label="Mail Id required"
                        placeholder="Select Mail Id"
                        required
                        search
                        options={trueOrFalseFormatted}
                      />
                    </Grid>
                    {values.mailIdRequired === "Yes" && (
                      <Grid item xs={12} sm={6} md={4}>
                        <FormikSelectInput
                          name="mailIdDomain"
                          label="Mail ID domain"
                          placeholder="Select Mail ID domain"
                          required={values.mailIdRequired === "Yes"}
                          search
                          options={mapOptions(domainOrgList) || []} // to change this domain
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="assetRequired"
                        label="Asset Required"
                        placeholder="Select Asset Required"
                        required
                        search
                        options={trueOrFalseFormatted || []}
                      />
                    </Grid>

                    {values.assetRequired === "Yes" && (
                      <Grid item xs={12} sm={6} md={4}>
                        <FormikSelectInput
                          name="asset"
                          label="Asset"
                          placeholder="Select Asset"
                          required={values.assetRequired === "Yes"}
                          search
                          options={assetOptions || []}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikSelectInput
                        name="mobileAccess"
                        label="Mobile Access"
                        placeholder="Select Mobile Access"
                        required
                        search
                        options={trueOrFalseFormatted || []}
                      />
                    </Grid>
                  </Grid>
                </Box>

                <Box mb={2} display="flex" justifyContent="flex-end" gap={2}>
                  <StyledButton
                    label="Reset"
                    type="reset"
                    variant="contained"
                    disabled={isSubmitting}
                  />

                  <StyledButton
                    label="Submit"
                    type="submit"
                    variant="outlined"
                    isSubmitting={isSubmitting}
                  />
                </Box>
              </Form>
            );
          }}
        </Formik>
      </CardWithActions>

      {shiftModalOpen && (
        <ShiftModal open={shiftModalOpen} setOpen={setShiftModalOpen} />
      )}
    </Box>
  );
};

export default AddEmployeeForm;
